import { clsx } from 'clsx';
import React, { ElementType, Suspense } from 'react';

import { ButtonLink } from '@/vibes/soul/primitives/button-link';
import { Image } from '~/components/image';
import { Link } from '~/components/link';

type BannerVariant = 'large' | 'medium';

type SizeChart = {
  large: { width: number; height: number };
  medium: { width: number; height: number };
}

function MainBannerSkeleton({ variant, className }: { variant: BannerVariant; className?: string }) {
  return (
    <section
      className={clsx(
        'relative overflow-hidden @container bg-contrast-100',
        { large: 'max-h-120', medium: 'max-h-94' }[variant],
        className,
      )}
      aria-hidden
    >
      <div className={clsx('h-full w-full', { large: 'max-h-120', medium: 'max-h-94' }[variant])}>
        <div className="h-full w-full animate-pulse bg-contrast-200" />
      </div>
      <div className={clsx(
        'absolute bottom-0 p-6 lg:p-12',
        { large: 'pb-12', medium: 'pb-8' }[variant]
      )}>
        <div className="mb-4 h-8 w-3/4 max-w-md animate-pulse rounded bg-contrast-300" />
        <div className="mb-5 h-4 w-1/2 max-w-sm animate-pulse rounded bg-contrast-300" />
        <div className="h-12 w-32 animate-pulse rounded bg-contrast-300" />
      </div>
    </section>
  );
}

interface ImageProps {
  size: BannerVariant;
  imgSrc: string;
  imgAlt: string;
}

export interface MainBannerProps {
  cta: {
    link: { href: string; target?: '_blank' | '_self' };
    linkText: string;
  };
  background: {
    image: string | undefined;
    alt: string;
  };
  variant: BannerVariant;
  className?: string;
  content: {
    heading: string;
    description?: string;
    headingTagName?: ElementType;
  };
  fetchPriority?: boolean;
}

interface ImageWithSizesProps extends ImageProps {
  className?: string;
}

export function MainBanner({ className, cta, background, content, variant, fetchPriority }: MainBannerProps) {
  const { link, linkText } = cta;
  const { image, alt } = background;
  const { heading, description, headingTagName: HeadingTag = 'div' } = content;
  const sizeMap: SizeChart = {
    large: { width: 1344, height: 512 },
    medium: { width: 656, height: 348 },
  };

  const ImageWithSizes: React.FC<ImageWithSizesProps> = ({ size, imgSrc, imgAlt, className }) => {
    const { width, height } = sizeMap[size];

    // Responsive sizes for banners
    const getImageSizes = (variant: BannerVariant) => {
      if (variant === 'large') {
        // Large banner: responsive width based on container max-width
        return '(min-width: 1536px) 1344px, (min-width: 1024px) 90vw, 100vw';
      } else {
        // Medium banner: full width on mobile/tablet, half width only after 1280px
        return '(min-width: 1536px) 656px, (min-width: 80rem) 50vw, 100vw';
      }
    };

    return (
      <Image
        alt={imgAlt}
        className={clsx("object-cover min-w-full", className)}
        height={height}
        src={imgSrc}
        width={width}
        sizes={getImageSizes(size)}
        quality={60}
        fetchPriority={fetchPriority ? 'high' : 'auto'}
        loading={fetchPriority ? 'eager' : 'lazy'}
      />
    );
  };

  const skeleton = <MainBannerSkeleton variant={variant} className={className} />;

  if (!image) {
    return skeleton;
  }

  return (
    <Suspense fallback={skeleton}>
      <section
        className={clsx(
          'relative overflow-hidden @container bg-contrast-100',
          { large: 'max-h-120', medium: 'max-h-94' }[variant],
          className,
        )}
      >
        <div>
          <ImageWithSizes imgAlt={alt} imgSrc={image} size={variant} className={
            clsx("h-full max-w-none", {
              'max-h-120': variant === 'large',
              'max-h-94': variant === 'medium',
            })} />
        </div>
        {/* Shadow overlay */}
        <Link href={link.href} target={link.target} className="block absolute inset-0 bg-shadow-primary" aria-label={linkText} />

        <div className={clsx("absolute bottom-0 p-6 text-white lg:p-12", {
          'pb-12': variant === 'large',
          'pb-8': variant === 'medium',
        })}>
          <HeadingTag
            className={clsx(
              'font-[family-name:var(--font-family-heading)] mb-4',
              { large: 'text-5xl leading-none', medium: 'text-2xl lg:text-4xl leading-none' }[variant],
            )}
          >
            {heading}
          </HeadingTag>
          <div className="mx-auto mb-5 font-[family-name:var(--font-family-body)] font-semibold max-w-xl">
            {description}
          </div>
          <ButtonLink
            href={link.href}
            shape="square"
            size="medium"
            target={link.target}
            variant="primary"
            className="px-8"
          >
            {linkText}
          </ButtonLink>
        </div>
      </section>
    </Suspense>
  );
}
